/**
 * Shared supersample→downscale rasterization step for the ad capture paths
 * (`capture-svg-preview.ts` and `capture-html-preview.ts`). Both used to read
 * the PNG blob directly off a 2× canvas, which produced files at twice the ad's
 * design canvas — e.g. a 160×600 ad exported at 320×1200 — violating
 * fixed-pixel platform specs. Centralized here so the two paths can't diverge.
 */

/**
 * Supersample factor used for crisp anti-aliasing before downscaling. The 2×
 * was inherited from the original SVG capture pattern as a "don't render blurry"
 * measure; we keep it but downscale to exact spec dims so output isn't 2× size.
 */
const SUPERSAMPLE_SCALE = 2;

/**
 * Rasterize a loaded image onto a canvas at exactly `width` × `height`: a 2× supersample
 * pass, then a high-quality downscale. Split from the PNG blob step so pixel callers
 * (the PSD export) share this path.
 */
export function rasterizeImageToCanvas(args: {
  image: HTMLImageElement;
  width: number;
  height: number;
}): HTMLCanvasElement {
  const { image, width, height } = args;

  const supersampleCanvas = document.createElement("canvas");
  supersampleCanvas.width = width * SUPERSAMPLE_SCALE;
  supersampleCanvas.height = height * SUPERSAMPLE_SCALE;
  const supersampleCtx = supersampleCanvas.getContext("2d");
  if (!supersampleCtx) {
    throw new Error("rasterizeImageToCanvas: 2D canvas context unavailable");
  }
  supersampleCtx.scale(SUPERSAMPLE_SCALE, SUPERSAMPLE_SCALE);
  supersampleCtx.drawImage(image, 0, 0, width, height);

  const outputCanvas = document.createElement("canvas");
  outputCanvas.width = width;
  outputCanvas.height = height;
  const outputCtx = outputCanvas.getContext("2d");
  if (!outputCtx) {
    throw new Error("rasterizeImageToCanvas: 2D canvas context unavailable");
  }
  outputCtx.imageSmoothingEnabled = true;
  outputCtx.imageSmoothingQuality = "high";
  outputCtx.drawImage(supersampleCanvas, 0, 0, width, height);

  return outputCanvas;
}

/** Rejects when the browser returns a null blob. */
export function canvasToPngBlob(canvas: HTMLCanvasElement): Promise<Blob> {
  return new Promise<Blob>((resolve, reject) => {
    canvas.toBlob((blob) => {
      if (blob) {
        resolve(blob);
      } else {
        reject(new Error("Canvas toBlob returned null"));
      }
    }, "image/png");
  });
}

/** Rasterize a loaded image to a PNG `Blob`. */
export async function rasterizeImageToBlob(args: {
  image: HTMLImageElement;
  width: number;
  height: number;
}): Promise<Blob> {
  return canvasToPngBlob(rasterizeImageToCanvas(args));
}
